import React from 'react';
import { Modal, Stack, Text, Button, Select, Group, Badge, NumberInput, Checkbox } from '@mantine/core';
import { useTranslation } from 'react-i18next';

export default function PlanningSettingsModal({ opened, onClose, profile = 'default', onChangeProfile, onSaveProfile, mediaMaxContinuousHours = 6, mediaMinBreakMinutes = 20, mediaDailyMaxHours = 10, mediaMinDailyRestHours = 11, mediaAmplitudeMaxHours = 11, mediaMaxConsecutiveDays = 6, mediaMinWeeklyDaysOff = 2, weeklyDefaultInternal = 40, weeklyDefaultExternal = 44, locationMinBreakMinutes = 30, mediaSeriesConsistencyEnabled = false, mediaSeriesConsistencyPenalty = 0.4, onChangeMediaMaxContinuousHours, onChangeMediaMinBreakMinutes, onChangeMediaDailyMaxHours, onChangeMediaMinDailyRestHours, onChangeMediaAmplitudeMaxHours, onChangeMediaMaxConsecutiveDays, onChangeMediaMinWeeklyDaysOff, onChangeWeeklyDefaultInternal, onChangeWeeklyDefaultExternal, onChangeLocationMinBreakMinutes, onChangeMediaSeriesConsistencyEnabled, onChangeMediaSeriesConsistencyPenalty }) {
  const { t } = useTranslation('common');
  const [localProfile, setLocalProfile] = React.useState(profile || 'default');
  const [localMediaMax, setLocalMediaMax] = React.useState(Number(mediaMaxContinuousHours) || 6);
  const [localMediaBreak, setLocalMediaBreak] = React.useState(Number(mediaMinBreakMinutes) || 20);
  const [localDailyMax, setLocalDailyMax] = React.useState(Number(mediaDailyMaxHours) || 10);
  const [localMinDailyRest, setLocalMinDailyRest] = React.useState(Number(mediaMinDailyRestHours) || 11);
  const [localAmplitudeMax, setLocalAmplitudeMax] = React.useState(Number(mediaAmplitudeMaxHours) || 11);
  const [localMaxConsecutiveDays, setLocalMaxConsecutiveDays] = React.useState(Number(mediaMaxConsecutiveDays) || 6);
  const [localMinWeeklyDaysOff, setLocalMinWeeklyDaysOff] = React.useState(Number(mediaMinWeeklyDaysOff) || 2);
  const [localWeeklyDefaultInternal, setLocalWeeklyDefaultInternal] = React.useState(Number(weeklyDefaultInternal) || 40);
  const [localWeeklyDefaultExternal, setLocalWeeklyDefaultExternal] = React.useState(Number(weeklyDefaultExternal) || 44);
  const [localLocationMinBreak, setLocalLocationMinBreak] = React.useState(Number(locationMinBreakMinutes) || 30);
  const [localSeriesConsistencyEnabled, setLocalSeriesConsistencyEnabled] = React.useState(!!mediaSeriesConsistencyEnabled);
  const [localSeriesConsistencyPenalty, setLocalSeriesConsistencyPenalty] = React.useState(Number(mediaSeriesConsistencyPenalty) || 0.4);
  React.useEffect(() => setLocalProfile(profile || 'default'), [profile]);
  React.useEffect(() => setLocalMediaMax(Number(mediaMaxContinuousHours) || 6), [mediaMaxContinuousHours]);
  React.useEffect(() => setLocalMediaBreak(Number(mediaMinBreakMinutes) || 20), [mediaMinBreakMinutes]);
  React.useEffect(() => setLocalDailyMax(Number(mediaDailyMaxHours) || 10), [mediaDailyMaxHours]);
  React.useEffect(() => setLocalMinDailyRest(Number(mediaMinDailyRestHours) || 11), [mediaMinDailyRestHours]);
  React.useEffect(() => setLocalAmplitudeMax(Number(mediaAmplitudeMaxHours) || 11), [mediaAmplitudeMaxHours]);
  React.useEffect(() => setLocalMaxConsecutiveDays(Number(mediaMaxConsecutiveDays) || 6), [mediaMaxConsecutiveDays]);
  React.useEffect(() => setLocalMinWeeklyDaysOff(Number(mediaMinWeeklyDaysOff) || 2), [mediaMinWeeklyDaysOff]);
  React.useEffect(() => setLocalWeeklyDefaultInternal(Number(weeklyDefaultInternal) || 40), [weeklyDefaultInternal]);
  React.useEffect(() => setLocalWeeklyDefaultExternal(Number(weeklyDefaultExternal) || 44), [weeklyDefaultExternal]);
  React.useEffect(() => setLocalLocationMinBreak(Number(locationMinBreakMinutes) || 30), [locationMinBreakMinutes]);
  React.useEffect(() => setLocalSeriesConsistencyEnabled(!!mediaSeriesConsistencyEnabled), [mediaSeriesConsistencyEnabled]);
  React.useEffect(() => setLocalSeriesConsistencyPenalty(Number(mediaSeriesConsistencyPenalty) || 0.4), [mediaSeriesConsistencyPenalty]);

  return (
    <Modal opened={opened} onClose={onClose} title={t('planning.settings.title')}>
      <Stack>
        <div>
          <Text fw={600} mb="xs">{t('planning.settings.profile.title')}</Text>
          <Select
            label={t('planning.settings.profile.preset_label')}
            value={localProfile}
            onChange={(v) => setLocalProfile(v || 'default')}
            data={[
              { value: 'default', label: t('planning.settings.profile.options.default') },
              { value: 'media', label: t('planning.settings.profile.options.media') },
            ]}
            withinPortal
          />
          <Group gap={8} mt={8}>
            <Badge variant="light">{t('planning.settings.profile.badge.default')}</Badge>
            <Badge variant="light">{t('planning.settings.profile.badge.media')}</Badge>
          </Group>
        </div>
        <div>
          <Text fw={600} mb="xs">{t('planning.settings.weekly_defaults.title')}</Text>
          <Group align="flex-end" gap="sm" wrap="wrap">
            <NumberInput
              label={t('planning.settings.weekly_defaults.internal')}
              min={0}
              max={168}
              step={1}
              value={localWeeklyDefaultInternal}
              onChange={(val) => setLocalWeeklyDefaultInternal(Number(val) || 0)}
              hideControls
            />
            <NumberInput
              label={t('planning.settings.weekly_defaults.external')}
              min={0}
              max={168}
              step={1}
              value={localWeeklyDefaultExternal}
              onChange={(val) => setLocalWeeklyDefaultExternal(Number(val) || 0)}
              hideControls
            />
          </Group>
        </div>
        <div>
          <Text fw={600} mb="xs">{t('planning.settings.general.title')}</Text>
          <Group align="flex-end" gap="sm" wrap="wrap">
            <NumberInput
              label={t('planning.settings.general.min_break_between_locations')}
              min={5}
              max={240}
              step={5}
              value={localLocationMinBreak}
              onChange={(val) => setLocalLocationMinBreak(Number(val) || 30)}
              hideControls
            />
          </Group>
        </div>
        {localProfile === 'media' && (
          <div>
            <Text fw={600} mb="xs">{t('planning.settings.media.title')}</Text>
            <Group align="flex-end" gap="sm" wrap="wrap">
              <NumberInput
                label={t('planning.settings.media.max_cont_hours')}
                min={1}
                max={16}
                step={0.5}
                value={localMediaMax}
                onChange={(val) => setLocalMediaMax(Number(val) || 6)}
                hideControls
              />
              <NumberInput
                label={t('planning.settings.media.min_break_minutes')}
                min={5}
                max={180}
                step={5}
                value={localMediaBreak}
                onChange={(val) => setLocalMediaBreak(Number(val) || 20)}
                hideControls
              />
              <NumberInput
                label={t('planning.settings.media.max_hours_per_day')}
                min={1}
                max={24}
                step={0.5}
                value={localDailyMax}
                onChange={(val) => setLocalDailyMax(Number(val) || 10)}
                hideControls
              />
              <NumberInput
                label={t('planning.settings.media.min_rest_between_days')}
                min={1}
                max={24}
                step={0.5}
                value={localMinDailyRest}
                onChange={(val) => setLocalMinDailyRest(Number(val) || 11)}
                hideControls
              />
              <NumberInput
                label={t('planning.settings.media.max_working_amplitude')}
                min={1}
                max={24}
                step={0.5}
                value={localAmplitudeMax}
                onChange={(val) => setLocalAmplitudeMax(Number(val) || 11)}
                hideControls
              />
              <NumberInput
                label={t('planning.settings.media.max_consecutive_days')}
                min={1}
                max={7}
                step={1}
                value={localMaxConsecutiveDays}
                onChange={(val) => setLocalMaxConsecutiveDays(Number(val) || 6)}
                hideControls
              />
              <NumberInput
                label={t('planning.settings.media.min_days_off_per_week')}
                min={0}
                max={7}
                step={1}
                value={localMinWeeklyDaysOff}
                onChange={(val) => setLocalMinWeeklyDaysOff(Number(val) || 2)}
                hideControls
              />
            </Group>
          </div>
        )}
        {localProfile === 'media' && (
          <div>
            <Text fw={600} mb="xs">{t('planning.settings.media.title')}</Text>
            <Group align="flex-end" gap="sm" wrap="wrap">
              <Checkbox
                label={t('planning.settings.media.series_consistency_enabled')}
                checked={localSeriesConsistencyEnabled}
                onChange={(e) => setLocalSeriesConsistencyEnabled(e.currentTarget.checked)}
              />
              <NumberInput
                label={t('planning.settings.media.series_consistency_weight')}
                min={0}
                step={0.1}
                value={localSeriesConsistencyPenalty}
                onChange={(val) => setLocalSeriesConsistencyPenalty(Number(val) || 0)}
                hideControls
                disabled={!localSeriesConsistencyEnabled}
              />
            </Group>
          </div>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={async () => {
            try {
              if (typeof onChangeProfile === 'function') onChangeProfile(localProfile);
              if (typeof onChangeMediaMaxContinuousHours === 'function') onChangeMediaMaxContinuousHours(localMediaMax);
              if (typeof onChangeMediaMinBreakMinutes === 'function') onChangeMediaMinBreakMinutes(localMediaBreak);
              if (typeof onChangeMediaDailyMaxHours === 'function') onChangeMediaDailyMaxHours(localDailyMax);
              if (typeof onChangeMediaMinDailyRestHours === 'function') onChangeMediaMinDailyRestHours(localMinDailyRest);
              if (typeof onChangeMediaAmplitudeMaxHours === 'function') onChangeMediaAmplitudeMaxHours(localAmplitudeMax);
              if (typeof onChangeMediaMaxConsecutiveDays === 'function') onChangeMediaMaxConsecutiveDays(localMaxConsecutiveDays);
              if (typeof onChangeMediaMinWeeklyDaysOff === 'function') onChangeMediaMinWeeklyDaysOff(localMinWeeklyDaysOff);
              if (typeof onChangeWeeklyDefaultInternal === 'function') onChangeWeeklyDefaultInternal(localWeeklyDefaultInternal);
              if (typeof onChangeWeeklyDefaultExternal === 'function') onChangeWeeklyDefaultExternal(localWeeklyDefaultExternal);
              if (typeof onChangeLocationMinBreakMinutes === 'function') onChangeLocationMinBreakMinutes(localLocationMinBreak);
              if (typeof onChangeMediaSeriesConsistencyEnabled === 'function') onChangeMediaSeriesConsistencyEnabled(localSeriesConsistencyEnabled);
              if (typeof onChangeMediaSeriesConsistencyPenalty === 'function') onChangeMediaSeriesConsistencyPenalty(localSeriesConsistencyPenalty);
              if (typeof onSaveProfile === 'function') await onSaveProfile(localProfile, {
                mediaMaxContinuousHours: localMediaMax,
                mediaMinBreakMinutes: localMediaBreak,
                mediaDailyMaxHours: localDailyMax,
                mediaMinDailyRestHours: localMinDailyRest,
                mediaAmplitudeMaxHours: localAmplitudeMax,
                mediaMaxConsecutiveDays: localMaxConsecutiveDays,
                mediaMinWeeklyDaysOff: localMinWeeklyDaysOff,
                weeklyDefaultInternal: localWeeklyDefaultInternal,
                weeklyDefaultExternal: localWeeklyDefaultExternal,
                locationMinBreakMinutes: localLocationMinBreak,
                mediaSeriesConsistencyEnabled: localSeriesConsistencyEnabled,
                mediaSeriesConsistencyPenalty: localSeriesConsistencyPenalty,
              });
            } finally {
              onClose?.();
            }
          }}>{t('common.save')}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
